import { Component, h, primitiveNodeTypes, useEffect, useRef, useState } from "@lukekaalim/act";
import { AnyLogicState } from "../../../defs/logic/all";
import { Logic, MiniTemplate } from "../../../defs";
import { TheatreState } from "../../../hooks/useTheatreState";
import { html } from "@lukekaalim/act-web";

import classes from './Mini.module.css';
import { LabeledInput } from "@kosmod/sesame";
import { TheatreContentManager } from "../../../hooks/useTheatreContentManager";
import { TheatreSceneResources } from "../../../hooks";
import { LogicEditor } from "./LogicEditor";
import { RenderLogicEditor } from "./Logic/RenderLogicEditor";
import { DoorEditor, VisibilitySourceEditor } from "./Logic/VisibilityLogicEditors";
import { PropsForTypeOfLogicEditor } from "./Logic/common";
import { CharacterLogicEditor, HeroLogicEditor, HitpointsLogicEditor, MonsterLogicEditor } from "./Logic/AdventureLogicEditor";
import { a3, ThreeJSRoot } from "@lukekaalim/act-three";
import { Graphics3DSetup, OrbitCamera, renderBeat } from "@kosmod/graphics-3d";

import * as three from 'three';
import { ID, useJeffect, useLogger } from "@kosmod/kit";
import { MiniRenderer } from "../../Theatre";

export type MiniTemplateEditorProps = {
  content: TheatreContentManager,
  resources: TheatreSceneResources,

  miniTemplate: Pick<MiniTemplate, 'logic' | 'name'>,
  graphics: Graphics3DSetup,

  state: TheatreState,

  onMiniTemplateChange(miniTemplate: Pick<MiniTemplate, 'logic' | 'name'>): void,
};

export const MiniTemplateEditor: Component<MiniTemplateEditorProps> = ({
  miniTemplate,
  onMiniTemplateChange,
  
  content, resources, graphics, state
}) => {
  // While editing the template, we write changes in the "local" template
  // on "save"/"autosave" or manual save, we write them back up to the parent callback
  const [localMiniTemplate, setLocalMiniTemplate] = useState(miniTemplate);

  const [isDirty, setIsDirty] = useState(false);

  const appendLogic = (newLogic: Logic.Any) => {
    setIsDirty(true);
    return setLocalMiniTemplate({
      ...localMiniTemplate,
      logic: [...localMiniTemplate.logic, newLogic]
    })
  }

  const addNewLogic = (key: AnyLogicState["key"]) => {
    switch (key) {
      case 'render':
        return appendLogic(Logic.create(key, { renderType: 'quick-sprite', spriteId: null }));
      case 'door':
        return appendLogic(Logic.create(key, { open: false }));
      case 'hitpoints':
        return appendLogic(Logic.create(key, { maximum: 100, current: 100, temporary: 0 }));
      case 'character':
        return appendLogic(Logic.create(key, { name: 'My Name' }));
      case 'hero':
        return appendLogic(Logic.create(key, { player: null }));
      case 'monster':
        return appendLogic(Logic.create(key, { sourceRef: false }));
      case 'movement':
        return appendLogic(Logic.create(key, { method: 'walking', speed: 6 }));
      case 'visibility-source':
        return appendLogic(Logic.create(key, { strength: 255 }));
    }
  }

  const miniPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const miniPreviewSceneRef = useRef<three.Scene | null>(null);
  const miniPreviewCameraRef = useRef<three.PerspectiveCamera | null>(null);

  const render = renderBeat.useBeat();

  const logger = useLogger('MiniEditor');

  useJeffect((j) => {
    const renderer = graphics.renderer.get();
    const sourceCanvas = graphics.canvas.get();

    const destinationCanvas = miniPreviewCanvasRef.get();
    const scene = miniPreviewSceneRef.get();
    const camera = miniPreviewCameraRef.get();

    if (!renderer || !sourceCanvas || !destinationCanvas || !scene || !camera)
      return;

    const draw2D = destinationCanvas.getContext('2d', { alpha: false  });
    if (!draw2D)
      return;

    const rect = destinationCanvas.getBoundingClientRect();
    destinationCanvas.width = rect.width;
    destinationCanvas.height = rect.height;

    j.add(render(() => {
      const destinationSize = { x: destinationCanvas.width, y: destinationCanvas.height };

      renderer.setScissor(0, 0, destinationSize.x, destinationSize.y);
      renderer.setViewport(0, 0, destinationSize.x, destinationSize.y);

      renderer.render(scene, camera);

      draw2D.drawImage(sourceCanvas,
        0, sourceCanvas.height - destinationCanvas.height,
          destinationCanvas.width, destinationCanvas.height,
        0, 0,
          destinationCanvas.width, destinationCanvas.height
      )

      renderer.setScissor(0, 0, sourceCanvas.clientWidth, sourceCanvas.clientHeight);
      renderer.setViewport(0, 0, sourceCanvas.clientWidth, sourceCanvas.clientHeight);
    }))
  }, [graphics])


  return h(html.section, {}, [
    // h(html.input, { value: test, onChange() {
    //   setTest(this.value);
    // } }),
    // h(html.input, { type: 'checkbox', checked: boot, onChange() {
    //   setBoot(this.checked);
    // } }),
    h(html.section, {}, [
      // h(html.button, {

      // }, 'Back'),
      h(html.button, { disabled: !isDirty, onClick() {
        onMiniTemplateChange(localMiniTemplate)
        setIsDirty(false);
      }}, 'Save'),
      h(html.button, { disabled: !isDirty, onClick(event) {
        setLocalMiniTemplate(miniTemplate)
        setIsDirty(false);
      },}, 'Revert'),

      !isDirty && h(html.span, {}, `No changes pending`),
      isDirty && h(html.span, {}, `Unsaved changes`),
    ]),
    h(html.form, { className: classes.miniTemplateEditor, onSubmit(e) {
      e.preventDefault();
      onMiniTemplateChange(localMiniTemplate);
    }, style: {
      // '--bg-color': `rgb(66, 44, 8)`,
      // '--fg-color': `rgb(214, 208, 150)`,
      // '--shadow-color': `rgba(255, 250, 220, 0.308)`,
      '--bg-color': `rgb(245, 242, 237)`,
      '--fg-color': `rgb(36, 37, 48)`,
      '--shadow-color': `rgba(51, 50, 47, 0.31)`,
      '--list-bg-color': `rgb(65, 65, 112)`
    } }, [
      h(html.section, { className: classes.miniTemplateCommonEditor }, [
        h(html.section, { className: classes.miniTemplateFormSection }, [
          h(LabeledInput, {
            label: 'Mini Name',
            value: localMiniTemplate.name,
            onInput() {
              setLocalMiniTemplate({ ...localMiniTemplate, name: this.value })
              setIsDirty(true);
            }
          })
        ]),
        h(html.canvas, { className: classes.miniTemplatePreviewWindow, ref: miniPreviewCanvasRef }),

        // Preview Logic
        h(primitiveNodeTypes.null, {}, h(ThreeJSRoot, {}, [
          h(a3.scene, { ref: miniPreviewSceneRef, background: new three.Color('white') }, [
            h(MiniRenderer, {
              mini: {
                id: ID('mini'),
                animation: null,
                rotation: 0,
                logic: localMiniTemplate.logic,
                networkState: 'ok',
                position: { type: 'grid', surfaceId: ID('surface'), vector: { x: -.5, y: -.5 } },
              },
              state,
            }),
            h(a3.ambientLight, { color: new three.Color('white') }),
            h(a3.Box3Helper, { box: new three.Box3(
              new three.Vector3(-.5, 0, -.5), 
              new three.Vector3(.5, 1, .5), 
            ), color: new three.Color('black') }),
            h(OrbitCamera, {
              ref: miniPreviewCameraRef,
              position: { x: 2, y: 2, z: 2 },
              // rotation: new three.Euler(-0.1 * Math.PI, 0, 0),
              aspect: 1
            })
          ]),
        ]))
      ]),
      h(html.section, { className: classes.miniTemplateLogicEditor }, [
        h(html.div, { className: classes.miniTemplateLogicToolbar }, [
          h(html.button, { type: 'button', onClick: () => addNewLogic('render') }, `Add Graphics Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('character') }, `Add Character Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('door') }, `Add Door Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('visibility-source') }, `Add Visibility Source Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('hero') }, `Add Hero Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('monster') }, `Add Monster Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('hitpoints') }, `Add Hitpoints Logic`),
          h(html.button, { type: 'button', onClick: () => addNewLogic('movement') }, `Add Movement Logic`),
        ]),
        h(html.ol, { className: classes.miniTemplateLogicList }, localMiniTemplate.logic.map((logic) => {
          const onLogicChange = (nextLogic: AnyLogicState) => {
            setLocalMiniTemplate({ ...localMiniTemplate, logic: localMiniTemplate.logic.map(l => l === logic ? nextLogic : l )})
            setIsDirty(true);
          }
          const onLogicDataChange = (nextLogicData: AnyLogicState["data"]) => {
            // we cheat a little here
            onLogicChange({ ...logic, data: nextLogicData } as any)
          }
          const onLogicDelete = () => {
            setLocalMiniTemplate({ ...localMiniTemplate, logic: localMiniTemplate.logic.filter(l => l !== logic)})
            setIsDirty(true);
          }
          const active = Logic.find(logic.key, localMiniTemplate.logic) === logic;

          const EDITORS = {
            'render': RenderLogicEditor,
            'door': DoorEditor,
            'visibility-source': VisibilitySourceEditor,
            'character': CharacterLogicEditor,
            'hitpoints': HitpointsLogicEditor,
            'monster': MonsterLogicEditor,
            'hero': HeroLogicEditor,
          } as Record<Logic.Key, Component<PropsForTypeOfLogicEditor<Logic.Key>>>

          return h(LogicEditor, {
            name: logic.key,
            onClickDelete: onLogicDelete,
            enabled: logic.enabled,
            active,
            onSetEnabled(enabled) {
              onLogicChange({ ...logic, enabled })
            },
          }, [
            h(EDITORS[logic.key], {
              content,
              resources,
              graphics: state.graphics,

              logic,
              onLogicChange,
              onLogicDataChange,
            }),
          ])
        })),
      ])
    ])
  ])

}

// export type MiniTemplate2DPreviewProps = {
//   miniTemplate: MiniTemplate,
//   state: TheatreState,
// }

// export const MiniTemplate2DPreview: Component<MiniTemplate2DPreviewProps> = ({ miniTemplate, state }) => {
//   const renderLogic = miniTemplate.logic.find(l => l.key === 'render');

//   if (!renderLogic)
//     return 'No Render Logic';

//   return [
//     // h(html.pre, {}, JSON.stringify({
//     //   data: renderLogic.data,
//     //   urls: [... state.sceneRes.miniSpritesURLs]
//     // }, null, 2)),
//     renderLogic.data.renderType === 'quick-sprite' && h(html.img, {
//       className: classes.preview,
//       src: state.sceneRes.miniSpritesURLs.get(renderLogic.data.spriteId)?.href || ''
//     })
//   ]
// };
// export const MiniTemplate3DPreview = () => {
//   return [

//   ]
// };


// export type MiniLogicEditorProps = {
//   logic: AnyLogicState,
// }

// export const MiniLogicEditor: Component<MiniLogicEditorProps> = () => {
//   return [
    
//   ]
// }